import React from 'react';
import { useSelector } from 'react-redux';
import { Box, CircularProgress, Typography, styled } from '@mui/material';
import { useResponsive } from 'src/hooks/useResponsive';
import { ApplicationState } from 'store/store';
import { addOpacity } from './helpers';

const StyledBox = styled(Box, {
    shouldForwardProp: prop =>
        !['isOverlay', 'isHidden', 'hasBackground', 'isFullScreen'].includes(prop as string)
})<{
    isOverlay?: boolean;
    isHidden?: boolean;
    hasBackground?: boolean;
    isFullScreen?: boolean;
}>(({ theme, isOverlay, isHidden, hasBackground, isFullScreen }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
    height: '100%',
    ...(isOverlay && {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: theme.palette.background.default,
        zIndex: 3,
        transition: theme.transitions.easing.easeIn,
        opacity: 1
    }),
    ...(isHidden && {
        opacity: 0,
        pointerEvents: 'none'
    }),
    ...(hasBackground && {
        backgroundColor: theme.palette.common.white
    }),
    ...(isFullScreen && {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: addOpacity(theme.palette.background.default, 0.5),
        zIndex: 3,
        transition: theme.transitions.easing.easeIn,
        opacity: 1
    })
}));

const StyledImage = styled('img')(() => ({
    width: '80%',
    maxWidth: '400px'
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
    marginTop: theme.spacing(2)
}));

interface IThrobberProps {
    text: string;
    isLoading?: boolean;
    isOverlay?: boolean;
    isFullScreen?: boolean;
}

export const Throbber: React.FC<React.PropsWithChildren<IThrobberProps>> = props => {
    const { text, isLoading, isOverlay, isFullScreen } = props;
    const { isDesktop } = useResponsive();
    const { loadingImage } = useSelector((state: ApplicationState) => state.settings);

    return (
        <StyledBox
            isOverlay={isOverlay}
            isHidden={isOverlay && !isLoading}
            hasBackground={isDesktop}
            isFullScreen={isFullScreen}
        >
            {loadingImage ? (
                <StyledImage src={loadingImage} alt="loading" />
            ) : (
                <CircularProgress variant="indeterminate" />
            )}
            <StyledTypography variant="subtitle1">{text}</StyledTypography>
        </StyledBox>
    );
};
